<template>
  <el-dialog
    id="create-modal"
    class="form-modal"
    :visible.sync="createVisible"
    v-loading="createProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="create-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Create Virtual Receptionist</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="createOperation">Create</el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
          >
            Cancel
          </el-button>
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          ></el-button>
        </div>
      </div>
    </div>

    <el-form>
      <el-tabs v-model="activeTabName">
        <el-tab-pane label="General" name="general">
          <div class="inline-form-group">
            <el-checkbox v-model="ivSpeechDetect" :label="true">
              <span>Enable speech detection</span>
            </el-checkbox>
          </div>
          <el-tag class="mb-10">
            <i class="el-icon-info"></i>
            Allow callers to speak key options
          </el-tag>

          <el-input v-model="createForm.name" placeholder="Enter name">
            <template slot="prefix">Name</template>
          </el-input>

          <div class="inline-form-group">
            <el-checkbox v-model="extensionDialing" :label="true">
              <span>Enable extension dialing</span>
            </el-checkbox>
          </div>
          <dads-select
            v-model="createForm.branchId"
            placeholder="Branch"
            filterable
          >
            <template slot="prefix"> Branch</template>
            <el-option
              v-for="branch in branchOptions"
              :key="branch.id"
              :value="branch.id"
              :label="branch.name"
            />
          </dads-select>

          <!-- TIMEOUT DESTINATION -->
          <h2 class="form-section-header">On timeout</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="createForm.timeoutDestinations"
            @destinationsSelected="onTimeoutDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <div>
            <el-select
              v-model="createForm.timeout"
              placeholder="Select timeout (seconds)"
            >
              <el-option v-for="i in 20" :key="i" :label="i" :value="i" />
              <template slot="prefix">Select timeout (seconds)</template>
            </el-select>
          </div>

          <!-- WRONGKEYPRESS DESTINATION -->
          <h2 class="form-section-header">If wrong key pressed</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="createForm.wrongKeyPressDestinations"
            @destinationsSelected="onWrongKeyPressDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <!-- LOOP SETTINGS -->
          <div class="mb-5">
            <h2 class="form-section-header">Loop settings</h2>
          </div>

          <div class="inline-form-group">
            <el-checkbox v-model="createForm.loopOnTimeout" :label="true">
              <span>Enable loop on timeout</span>
            </el-checkbox>
          </div>
          <div class="inline-form-group">
            <el-checkbox v-model="createForm.loopOnWrongKeyPress" :label="true">
              <span>Enable loop on wrong key</span>
            </el-checkbox>
          </div>

          <div
            v-if="createForm.loopOnTimeout || createForm.loopOnWrongKeyPress"
          >
            <el-select
              v-model="createForm.maxLoops"
              placeholder="Select max loops"
            >
              <template slot="prefix">Max loops</template>
              <el-option v-for="i in 5" :key="i" :value="i" :label="i" />
            </el-select>
          </div>
        </el-tab-pane>

        <!-- ⭐ MEDIAFILES ⭐ -->
        <el-tab-pane label="Media" name="media">
          <existing-mediafile
            title="Mediafiles"
            :multiple="!ivSpeechDetect"
            :clearState="clearFormFields"
            :newMediafile="newMediafile"
            @mediafilesSelected="onMediafilesSelected"
            @mediafileSelected="onMediafileSelected"
          />
          <el-tag v-if="ivSpeechDetect" class="mb-10">
            <i class="el-icon-info"></i>
            only one media file is supported when speech detect is enabled
          </el-tag>
          <new-mediafile @mediafileSaved="onNewMediafileCreation" />
        </el-tab-pane>

        <!-- KEYPRESS OPTIONS -->
        <el-tab-pane
          label="Key Press Options"
          name="keypress"
          class="key-press-options"
        >
          <div
            sytle="margin-bottom: 8px;"
            v-for="(key, i) in keyOptions"
            :key="i"
          >
            <h2 class="form-section-header">
              Key {{ getKeyLabel(key) }} Destinations
            </h2>
            <destination-selector
              :isTerminalDestSelector="true"
              :draggable="true"
              :showHeader="false"
              :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
              :selectedDestinations="createForm[`key${key}Destinations`]"
              @destinationsSelected="onKeyDestinationsSelection(key, $event)"
              title=""
              :clearFields="clearFormFields"
            />
          </div>
        </el-tab-pane>

        <el-tab-pane
          label="Announcements"
          name="announcements"
        >
          <draggable
            :key="draggableKey"
            @change="changeDraggableKey"
            v-model="announcementKeys"
          >
            <transition-group name="list" tag="div">
              <div
                class="inline-form-group"
                v-for="(item, i) in announcementKeys"
                :key="i + '$(^^)$'"
              >
                <el-col :span="18">
                  <dads-select
                    :placeholder="`Select Announcement ${i + 1}`"
                    :remote-method="(query) => remoteSearchAnnouncements(query, i)"
                    v-model="item.id"
                    remote
                    filterable
                    clearable
                    @change="($event) => onAnnouncementSelection($event, i)"
                  >
                    <el-option
                      v-for="(el, j) in item.options"
                      :key="j"
                      :label="el.name"
                      :value="el.id"
                    />
                  </dads-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="5" class="text-right">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Remove Announcement ${i + 1}`"
                    placement="top"
                  >
                    <el-button
                      class="ml-5"
                      style="float: right; margin-right: 1px"
                      @click="removeAnnouncementKey(i)"
                      icon="el-icon-delete"
                    />
                  </el-tooltip>
                  <el-button icon="el-icon-rank" type="default" />
                </el-col>
              </div>
            </transition-group>
          </draggable>
          <el-button
            type="text"
            icon="el-icon-circle-plus-outline"
            @click="addAnnouncementKey"
          >
            Add Announcement
          </el-button>
        </el-tab-pane>
      </el-tabs>
    </el-form>
  </el-dialog>
</template>

<script>
import Draggable from "vuedraggable";
import ExistingMediafile from "@/components/ExistingMediafile";
import NewMediafile from "@/components/NewMediafile";
import DestinationSelector from "@/components/DestinationSelector.vue";
import DadsSelect from "@/components/select";
import feathersClient, { apiV2Host } from "@/feathers/feathers-client";

export default {
  name: "CreateIvrForm",

  props: {
    branchOptions: { required: true },
    createVisible: { required: true },
    clearFormFields: { required: true },
    createProcessing: { required: true },
    reRenderKey: { required: true },
    draggableDestTypes: { required: true }
  },

  components: {
    Draggable,
    DadsSelect,
    NewMediafile,
    ExistingMediafile,
    DestinationSelector
  },

  data() {
    return {
      newMediafile: null,
      activeMediafileTabName: "existing",
      activeTabName: "general",
      keyOptions: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, "SHARP", "STAR"],
      greetingType: "",
      existingGreetingMsgName: "",
      ivSpeechDetect: false,
      extensionDialing: false,
      announcementOptions: null,
      draggableKey: "",
      announcementKeys: [
        {
          id: null,
          options: [],
          value: null,
        },
      ],
      createForm: {
        name: "",
        phoneNums: [],
        timeout: 10,
        timeoutDestinations: [],
        wrongKeyPressDestinations: [],
        loopOnTimeout: false,
        loopOnWrongKeyPress: false,
        maxLoops: null,
        allowDisa: false,
        key0Destinations: [],
        key1Destinations: [],
        key2Destinations: [],
        key3Destinations: [],
        key4Destinations: [],
        key5Destinations: [],
        key6Destinations: [],
        key7Destinations: [],
        key8Destinations: [],
        key9Destinations: [],
        keySHARPDestinations: [],
        keySTARDestinations: [],
        meIds: [],
        branchId: [],
        ivSpeechDetect: false,
        announcementsIds:[],
      }
    };
  },
  async created() {
    await this.getDefaultBranch();
  },
  computed: {
    selectedTenant() {

      return this.$store.state.selectedTenant;
    },

    timeoutDestinationsSelected() {
      return this.createForm.timeoutDestinations.length
        ? this.createForm.timeoutDestinations.findIndex(
            ({ id }) => id !== null
          ) !== -1
        : false;
    }
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
        this.getDefaultBranch();
      }
    },

    async createVisible(value) {
      if (value) {
        await this.getAnnouncementOptions();
      }
    },

    timeoutDestinationsSelected(val) {
      if (!val) {
        this.createForm.timeout = null;
      }
    }
  },

  methods: {
    changeDraggableKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      this.draggableKey = `${randomString()}-${randomString()}$${randomString()}`;
    },

    removeAnnouncementKey(idx) {
      this.announcementKeys.splice(idx, 1);
    },

    async remoteSearchAnnouncements(query, idx) {
      if (query && query.length) {
        try {
          const res = await fetch(`${apiV2Host}/admin/announcements?tenantId=${this.selectedTenant.id}&name=${query}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
              "Content-Type": "application/json",
            },
          });
          if (res.ok) {
            const result = await res.json();
            this.announcementKeys[idx].options = [...result.records];
          }
        } catch (error) {
          this.$notify({
            title: error.name,
            message: error.message,
            type: "error"
          });
        }
      }
    },

    onAnnouncementSelection(announcementId, i) {
      if (!announcementId) {
        this.announcementKeys[i].value = null;
        return;
      }

      const announcement = this.announcementKeys[i].options.find(
        (an) => an.id === announcementId
      );

      this.announcementKeys[i].value = {
        id: announcementId,
        name: announcement.name,
        number: announcement.number,
      };
    },

    async getDefaultBranch() {
      try {
        const [defaultBranch] = await feathersClient.service("branches").find({
          query: {
            tenantId: this.selectedTenant.id,
            default: 1
          }
        });
        this.createForm.branchId = defaultBranch.id;
      } catch (error) {
        console.log(error)
      }
    },

    addAnnouncementKey() {
      // Add new field and pre-populate with announcement options
      this.announcementKeys.push({
        options: [...this.announcementOptions],
        id: null,
        value: null,
      });
    },

    async getAnnouncementOptions() {
      try {
        const response = await fetch(
          `${apiV2Host}/admin/announcements?tenantId=${this.selectedTenant.id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch announcements");
        }

        const { records } = await response.json();
        this.announcementOptions = records;
        this.announcementKeys[0].options = [...records];

      } catch (error) {
        this.$notify({
          title: error.name,
          message: error.message,
          type: 'error'
        });
      }
    },

    onNewMediafileCreation(newMediafile) {
      this.newMediafile = newMediafile;
    },

    onTimeoutDestinationsSelection(destinations) {
      this.createForm.timeoutDestinations = [...destinations];
    },

    onWrongKeyPressDestinationsSelection(destinations) {
      this.createForm.wrongKeyPressDestinations = [...destinations];
    },

    onKeyDestinationsSelection(key, destinations) {
      this.createForm[`key${key}Destinations`] = [...destinations];
    },

    getKeyLabel(key) {
      return typeof key === "string" ? (key === "SHARP" ? "#" : "*") : key;
    },

    getRandomKey() {
      const randomString = () =>
        Math.random()
          .toString(36)
          .slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },

    onMediafilesSelected(meIds) {
      this.createForm.meIds = [...meIds];
    },

    onMediafileSelected(meIds) {
      this.createForm.meIds = [meIds];
    },

    clearFields() {
      this.activeTabName = "general";
      this.newMediafile = null;
      this.activeMediafileTabName = "existing";
      Object.keys(this.createForm).forEach(key => {
        if (typeof this.createForm[key] === "string") {
          this.createForm[key] = "";
        }
        if (typeof this.createForm[key] === "boolean") {
          this.createForm[key] = false;
        }
        if (typeof this.createForm[key] === "number") {
          this.createForm[key] = null;
        }
        if (Array.isArray(this.createForm[key])) {
          this.createForm[key] = [];
        }
      });

      this.keyOptions.forEach(key => {
        this.createForm[`key${key}Destinations`] = [];
      });
      this.createForm.timeoutDestinations = [];
      this.createForm.wrongKeyPressDestinations = [];
      this.createForm.announcementsIds = [];
      this.createForm.timeout = 10;
      this.ivSpeechDetect = false;
      this.createForm.branchId = null;
      this.extensionDialing = false;
      this.announcementKeys = [
        {
          id: null,
          options: [],
          value: null,
        },
      ];
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find(dst => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Do not leave any destination field empty.",
          type: "error"
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        destinations.forEach((dst, idx) => (dst.order = idx + 1));

        finalDestinations = destinations.map(({ id, type, order }) => ({
          id,
          type,
          order
        }));
      }
      return finalDestinations;
    },

    createOperation() {
      this.createForm.allowDisa = this.extensionDialing ? "on" : "";
      this.createForm.loopOnTimeout = this.createForm.loopOnTimeout ? "on" : "";
      this.createForm.loopOnWrongKeyPress = this.createForm.loopOnWrongKeyPress
        ? "on"
        : "";
      this.createForm.maxLoops = this.createForm.maxLoops || 2;
      this.createForm.timeout = this.createForm.timeout || 10;
      this.createForm.ivSpeechDetect = this.ivSpeechDetect ? 1 : 0;

      // Map Announcement ids to array of ids
      this.createForm.announcementsIds = this.announcementKeys
        .filter(item => item.id !== null)
        .map(item => item.id);

      const createBody = { ...this.createForm };
      const wrongKeyPressDestinations = this.validateDestinations(
        createBody.wrongKeyPressDestinations,
        "Wrong Key Press"
      );

      const timeoutDestinations = this.validateDestinations(
        createBody.timeoutDestinations,
        "Timeout"
      );

      if (wrongKeyPressDestinations && timeoutDestinations) {
        createBody.timeoutDestinations = timeoutDestinations;
        createBody.wrongKeyPressDestinations = wrongKeyPressDestinations;
      } else {
        return;
      }

      let validatedDestinations = {};
      for (let i = 0; i < this.keyOptions.length; i++) {
        const key = this.keyOptions[i];
        validatedDestinations[
          `key${key}Destinations`
        ] = this.validateDestinations(
          this.createForm[`key${key}Destinations`],
          `Key ${key}`
        );
      }

      let keyPressDestinationsAreValid = true;
      for (let i = 0; i < this.keyOptions.length; i++) {
        const key = this.keyOptions[i];
        const prefix = `key${key}Destinations`;
        const destinations = validatedDestinations[prefix];
        if (destinations) {
          createBody[prefix] = [...destinations];
        } else {
          keyPressDestinationsAreValid = false;
          break;
        }
      }

      if (keyPressDestinationsAreValid) {
        this.$emit("createOperation", createBody);
      }
    },

    cancelOperation() {
      this.newMediafileKey = this.getRandomKey();
      this.existingMediafileKey = this.getRandomKey();
      this.$emit("cancelOperation", "create");
    },

    remoteMethodForAvailableNums(query) {
      this.$emit("remoteMethodForAvailableNums", query);
    }
  },

  filters: {
    formatNumber(value) {
      return `(${value.substr(0, 3)}) ${value.substr(3, 3)}-${value.substr(
        6,
        4
      )}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.checkbox-group {
  max-width: 600px;
  margin: auto;
}

.el-tabs {
  max-width: 600px;
  margin: auto;
}

.inline-form-group .el-checkbox {
  margin-left: 0;
}
</style>
